import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum POv3LineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, reference ID, etc
  name = 'name', // descriptive name of the item
  description = 'description', // verbose description of the item (human parseable)
  itemQty = 'itemQty', // quantity of the item for order
  unitPrice = 'unitPrice', // price per unit
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
}

export const POv3addressInfoSchema = yup.object().shape({
  attn: yup.string().nullable().optional(),
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  zipCode: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
  phoneNumber: yup.string().nullable().optional(),
  emailAddress: yup.string().nullable().optional(),
  displayMultipleLines: yup.string().nullable().optional(),
})
export type POv3FileAddressData = yup.InferType<typeof POv3addressInfoSchema>

export const POv3FileDataSchema = yup.object().shape({
  issuer: POv3addressInfoSchema,
  orderNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  freightTerms: yup.string().nullable().optional(),
  destination: POv3addressInfoSchema,
  vendor: POv3addressInfoSchema,
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        name: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .required(),
  items2: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        name: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
      }),
    )
    .optional(),
  dontRoundUnitPrice: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  useLocaleQuantityFormatting: yup.string().nullable().optional(),
})

export type POv3FileData = yup.InferType<typeof POv3FileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<POv3FileData>

export const POv3FileMappings: RequiredFileData = {
  issuer: {
    attn: '8ce73d71-07cb-4b25-9f27-8ee9eb487676',
    companyName: '4073f1bf-b9f7-4f66-acc2-7312e9f2df32',
    addressLine1: 'd1e3714a-59d8-412e-a16c-92abd0d8f478',
    addressLine2: 'ffaf5d3f-a3ca-4b08-bb50-726eb82b26dd',
    city: '95bb0fe2-1528-40d7-850e-c9a76e27a9ca',
    state: '905dbe57-65a4-4fdc-a1c0-26a67b88e9c2',
    zipCode: 'ae3947d0-425c-4097-b4e9-a6c7e08c5ac2',
    country: 'afc41d5c-359e-4d21-b382-cbb05b1c09db',
    phoneNumber: '63733455-763c-434e-9707-1d3b1da12a76',
    emailAddress: '4d6061c6-f5e0-4484-badc-d2d3794eb421',
    displayMultipleLines: 'd3c587d8-48a5-45fd-9603-ce536fc90434',
  },
  orderNumber: '38675769-86f1-4756-a65c-be4de7c66fa5',
  paymentTerms: 'd671fbd7-dfab-40f1-a89e-f9b56802663d',
  freightTerms: 'fff65f49-0eda-42ca-97e8-8ac94025367f',
  dateIssued: 'cd6e7565-5a67-4e37-bbf1-431064ef78e9',
  dateReady: '82cfcf88-6b08-461c-96d6-439007a42d96',
  destination: {
    attn: '7d8275f0-249c-4a13-8ff5-333c2b309e58',
    companyName: '8a9aaa07-ecdd-4a63-b6bf-d9d0feb7ce12',
    addressLine1: '9cac5c54-0c5f-474e-977f-2a466bb35586',
    addressLine2: '3bde2316-9c7d-42a7-a62a-051b21ae150e',
    city: 'de93b5b1-9b42-42f2-abea-f7fdd22461d3',
    state: '65e9a3e0-b676-4b55-8053-a977c3affe6b',
    zipCode: '998882a1-1c9b-4d91-80d7-ca211b9c6d56',
    country: '88943f87-fd53-44fe-9f93-83e3abc8c59b',
    phoneNumber: 'e5bc4bd0-c044-4134-96d2-8f48bca48cf9',
    emailAddress: 'd55a15f9-57d7-401f-a662-0ac29fcba772',
    displayMultipleLines: '68a3860a-0972-4d54-8d46-edb9aab2bf6a',
  },
  vendor: {
    attn: 'a95b12dd-65ae-41d3-915a-b3426abdf4c5',
    companyName: '7813d4bf-5ed7-47a7-bd4e-514a64e39d5a',
    addressLine1: '2888a525-8289-4a59-aebc-bf63775fe19b',
    addressLine2: 'fd767cbe-e720-4d2c-9799-fd4ee69080f5',
    city: 'a5906477-4778-4a75-b047-953168ff1eee',
    state: 'c2045c22-345d-4f86-b9a6-06d9ab7892b1',
    zipCode: 'c6237d21-6982-4e77-9b67-05cdfa1846fb',
    country: '77d7e2a2-e2a4-4bc3-8b52-7d86f05056af',
    phoneNumber: '69b86f14-9ba8-4b15-9016-a11c8b203d10',
    emailAddress: 'acb79253-03d2-49dc-bc7b-267556095269',
    displayMultipleLines: 'eb0093a8-2f76-4e25-9a1a-a877af10a3cb',
  },
  items: [
    {
      itemNumber: null,
      itemName: 'f5f4505d-2ce0-4a54-9e9c-7bb7c812b2e0',
      name: '35a250b3-4a97-4446-aedc-0138f7fffe28',
      description: '1e63225b-2270-4484-9af0-b647d66bf028',
      itemQty: 'f4e6d1f0-231c-439b-8d01-fbd9abcda835',
      unitPrice: '4aba53a7-f1e0-4fed-b059-73346bbaee9e',
      lineTotal: null,
    },
  ],
  items2: [
    {
      itemNumber: null,
      itemName: '2a8e4f15-6d9c-4b7a-8e3f-9c5d7a2b1e4f',
      name: 'b7c9d3e5-1a4f-4c8b-9e2d-7f6a3b5c8d1e',
      description: 'e4f7a9b2-5c6d-4e8a-b1c3-d5e7f9a2b4c6',
      itemQty: 'c8d1e3f5-7a9b-4c2d-8e6f-b3c5d7e9f1a2',
      unitPrice: 'f9a2b4c6-8d1e-4f3a-b5c7-d9e1f3a5b7c9',
      lineTotal: null,
    },
  ],
  dontRoundUnitPrice: 'ac9614ba-5666-461f-843d-9b212ee51158',
  agreement: '49a93c3d-86ae-4823-8f73-325c8b1d3be3',
  instructions: '843a95a5-b08c-402c-8a08-99ea3d4e233c',
  useLocaleQuantityFormatting: 'ecbddb54-9d72-48a5-88fa-a262d7ba9bc0',
}
